import { FiCpu, FiInfo } from 'react-icons/fi';
import { useGetOrgAiUsageQuery } from '@/store/api/apiSlice';

function formatUsd(amount: number): string {
  if (amount >= 1) return `$${amount.toFixed(2)}`;
  return `$${amount.toFixed(4)}`;
}

function displayName(user?: { firstName?: string; lastName?: string; email?: string } | null) {
  if (!user) return 'Unknown';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return name || user.email || 'Unknown';
}

export default function OrgAiUsagePanel() {
  const { data, isLoading, isError } = useGetOrgAiUsageQuery(undefined);

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 text-sm text-text-muted">
        Loading AI token usage…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="bg-white rounded-2xl border border-red-200 shadow-sm p-4 text-sm text-red-700">
        Could not load AI usage for this organization.
      </div>
    );
  }

  const totals = data.totals ?? { apiCalls: 0, totalTokens: 0, estimatedCost: 0 };
  const perSeat = data.perSeat ?? {
    billableSeats: 0,
    memberCount: 0,
    estimatedCostPerSeat: 0,
    tokensPerSeat: 0,
    apiCallsPerSeat: 0,
  };
  const byUser = data.byUser ?? [];
  const byEndpoint = data.byEndpoint ?? [];
  const monthlyTrend = data.monthlyTrend ?? [];

  return (
    <div id="ai-usage" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-4">
      <div className="flex flex-wrap items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
          <FiCpu className="w-4 h-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-bold text-text-primary">AI token consumption</h2>
          <p className="text-[11px] text-text-muted mt-0.5">
            Organization-wide token usage and estimated model cost — use per-seat figures to guide seat pricing.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">API calls</p>
          <p className="text-xl font-bold text-slate-900 mt-0.5">{totals.apiCalls.toLocaleString()}</p>
        </div>
        <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total tokens</p>
          <p className="text-xl font-bold text-slate-900 mt-0.5">{totals.totalTokens.toLocaleString()}</p>
        </div>
        <div className="rounded-xl border border-brand/20 bg-brand/5 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-brand">Est. cost</p>
          <p className="text-xl font-bold text-brand mt-0.5">{formatUsd(totals.estimatedCost)}</p>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Per billable seat</p>
          <p className="text-xl font-bold text-emerald-800 mt-0.5">{formatUsd(perSeat.estimatedCostPerSeat)}</p>
          <p className="text-[10px] text-emerald-700 mt-0.5">
            {perSeat.tokensPerSeat.toLocaleString()} tokens · {perSeat.billableSeats} seats
          </p>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
        <FiInfo className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <p>
          Estimated cost uses model list prices at call time (not your Razorpay invoice). Per-seat divides total cost by{' '}
          <strong>{perSeat.billableSeats}</strong> billable seat{perSeat.billableSeats === 1 ? '' : 's'} (
          {perSeat.memberCount} active members).
        </p>
      </div>

      {monthlyTrend.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Last 6 months</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-text-muted border-b border-border">
                  <th className="py-2 pr-4">Month</th>
                  <th className="py-2 pr-4 text-right">Calls</th>
                  <th className="py-2 pr-4 text-right">Tokens</th>
                  <th className="py-2 text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {monthlyTrend.map((row: { month: string; calls: number; tokens: number; cost: number }) => (
                  <tr key={row.month} className="border-b border-border/40">
                    <td className="py-2 pr-4 font-medium text-text-primary">{row.month}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{row.calls.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{row.tokens.toLocaleString()}</td>
                    <td className="py-2 text-right tabular-nums font-semibold text-brand">{formatUsd(row.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {byUser.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">By member</h3>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="min-w-full text-xs">
              <thead className="bg-surface-hover/50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-text-muted">Member</th>
                  <th className="px-3 py-2 text-right font-semibold text-text-muted">Calls</th>
                  <th className="px-3 py-2 text-right font-semibold text-text-muted">Tokens</th>
                  <th className="px-3 py-2 text-right font-semibold text-text-muted">Cost</th>
                </tr>
              </thead>
              <tbody>
                {byUser
                  .sort((a: { estimatedCost: number }, b: { estimatedCost: number }) => b.estimatedCost - a.estimatedCost)
                  .slice(0, 15)
                  .map((row: {
                    user?: { email?: string; firstName?: string; lastName?: string };
                    calls: number;
                    totalTokens: number;
                    estimatedCost: number;
                  }) => (
                    <tr key={row.user?.email ?? displayName(row.user)} className="border-t border-border/40">
                      <td className="px-3 py-2">
                        <p className="font-medium text-text-primary">{displayName(row.user)}</p>
                        {row.user?.email && (
                          <p className="text-[10px] text-text-muted truncate">{row.user.email}</p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{row.calls}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{row.totalTokens.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatUsd(row.estimatedCost)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {byEndpoint.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">By feature</h3>
          <div className="flex flex-wrap gap-2">
            {byEndpoint
              .sort((a: { estimatedCost: number }, b: { estimatedCost: number }) => b.estimatedCost - a.estimatedCost)
              .slice(0, 8)
              .map((row: { endpoint: string; calls: number; totalTokens: number; estimatedCost: number }) => (
                <span
                  key={row.endpoint}
                  className="inline-flex flex-col rounded-lg border border-border bg-surface px-3 py-2 text-[11px]"
                >
                  <span className="font-semibold text-text-primary truncate max-w-[200px]">{row.endpoint}</span>
                  <span className="text-text-muted mt-0.5">
                    {row.calls} calls · {row.totalTokens.toLocaleString()} tokens · {formatUsd(row.estimatedCost)}
                  </span>
                </span>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
